import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../../../contexts/AuthContext';
import { useNotificationTriggers } from '../../../../../../features/notifications';
import { openShareModal } from '../../../../../../features/share';
import { selectUsersEntities, fetchUserIfNeeded } from '../../../../../../redux/slices/usersCacheSlice';
import {
  fetchSolutionsFirstPage,
  fetchMoreSolutionsByPostId,
  toggleLikeSolution,
  toggleSaveSolution,
  selectSolutionsByPostId,
  selectSolutionsLoading,
  selectSolutionsError,
  selectSolutionsHasMore,
  selectSolutionsLoadingMore,
  selectSolutionsTotalCount
} from '../../../../../../redux/solutions';

const PAGE_SIZE = 5;

export const useSolutionDisplay = ({ postId, onSolutionCountChange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { triggerSolutionLikeNotification } = useNotificationTriggers();

  const [expandedPhases, setExpandedPhases] = useState({});
  const [highlightedId, setHighlightedId] = useState(null);
  const [liking, setLiking] = useState({});
  const [saving, setSaving] = useState({});
  const [sharing] = useState({});
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [, setUserCollapsed] = useState(false);

  const headerRef = useRef(null);
  const pendingScrollIndexRef = useRef(null);
  const listRef = useRef(null);
  const lastScrolledTargetRef = useRef(null);
  const attemptedRefetchRef = useRef(null);

  const usersEntities = useSelector(selectUsersEntities);
  const rawSolutions = useSelector((state) => selectSolutionsByPostId(state, postId));
  const solutions = useMemo(() => rawSolutions || [], [rawSolutions]);
  const loading = useSelector((state) => selectSolutionsLoading(state, postId));
  const error = useSelector((state) => selectSolutionsError(state, postId));
  const hasMore = useSelector((state) => selectSolutionsHasMore(state, postId));
  const loadingMore = useSelector((state) => selectSolutionsLoadingMore(state, postId));
  const totalCount = useSelector((state) => selectSolutionsTotalCount(state, postId));

  const isEmailLike = useCallback((str) => {
    if (!str || typeof str !== 'string') return false;
    const s = str.trim();
    return /@/.test(s) && /\./.test(s);
  }, []);

  const isPlaceholderProfession = useCallback((str) => {
    if (!str || typeof str !== 'string') return true;
    const s = str.trim().toLowerCase();
    return s === '' || s === 'no profession set' || s === 'unknown' || s === 'n/a';
  }, []);

  useEffect(() => {
    if (postId) {
      dispatch(fetchSolutionsFirstPage({ postId, pageSize: PAGE_SIZE }));
    }
  }, [dispatch, postId]);

  useEffect(() => {
    const scrollToSolutionId = location.state?.scrollToSolution;
    if (!scrollToSolutionId || !postId) return;

    if (attemptedRefetchRef.current === scrollToSolutionId) return;

    const solutionExists = solutions.some(sol => sol.id === scrollToSolutionId);
    if (!solutionExists && !loading) {
      attemptedRefetchRef.current = scrollToSolutionId;
      dispatch(fetchSolutionsFirstPage({ postId, pageSize: PAGE_SIZE }));
    }
  }, [location.state?.scrollToSolution, postId, solutions, loading, dispatch]);

  useEffect(() => {
    if (typeof onSolutionCountChange === 'function') {
      onSolutionCountChange(totalCount || 0);
    }
  }, [totalCount, onSolutionCountChange]);

  useEffect(() => {
    if (!solutions || solutions.length === 0) return;

    const userIdsToFetch = solutions
      .filter((sol) => {
        if (!sol?.userId) return false;
        const needsName = !sol?.creatorUsername || isEmailLike(sol.creatorUsername);
        const needsProf = isPlaceholderProfession(sol?.creatorProfession);
        return needsName || needsProf;
      })
      .map(sol => sol.userId);

    if (userIdsToFetch.length > 0) {
      if (window.batchedUserFetcher && window.batchedUserFetcher.dispatch) {
        userIdsToFetch.forEach(userId => window.batchedUserFetcher.fetchUser(userId));
      } else {
        userIdsToFetch.forEach((userId, index) => {
          setTimeout(() => dispatch(fetchUserIfNeeded({ userId, force: false })), index * 50);
        });
      }
    }
  }, [solutions, dispatch, isEmailLike, isPlaceholderProfession]);

  useEffect(() => {
    const idx = pendingScrollIndexRef.current;
    if (idx == null) return;
    if (!solutions || solutions.length <= idx) return;
    const target = solutions[idx];
    if (!target || !target.id) { pendingScrollIndexRef.current = null; return; }
    requestAnimationFrame(() => {
      const el = document.getElementById(`solution-${target.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    pendingScrollIndexRef.current = null;
  }, [solutions]);

  useEffect(() => {
    const idx = pendingScrollIndexRef.current;
    if (idx == null) return;
    if (!solutions || solutions.length <= idx) return;
    const target = solutions[idx];
    if (!target || !target.id) return;
    requestAnimationFrame(() => {
      const el = document.getElementById(`solution-${target.id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        pendingScrollIndexRef.current = null;
      }
    });
  }, [visibleCount, solutions]);

  useEffect(() => {
    const cameFromSolutionView = !!location.state?.fromSolutionView;
    const hashMatch = location.hash.match(/^#solution-(.+)$/);
    const solutionId = (hashMatch && hashMatch[1]) || location.state?.scrollToSolution;
    if (!solutionId) return;
    const shouldHighlight = cameFromSolutionView && solutionId === (hashMatch && hashMatch[1]);
    if (solutions.length === 0) return;

    const solutionExists = solutions.some(sol => sol.id === solutionId);

    if (!solutionExists && location.state?.scrollToSolution) {
      dispatch(fetchSolutionsFirstPage({ postId, pageSize: PAGE_SIZE }));
      return;
    }

    if (!solutionExists) return;

    const currentTarget = `${solutionId}-${location.hash}`;
    const alreadyScrolled = lastScrolledTargetRef.current === currentTarget;

    if (alreadyScrolled && !cameFromSolutionView) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30;

    const tryHighlight = () => {
      if (cancelled) return;
      attempts += 1;
      const el =
        document.querySelector(`[data-solution-id="${solutionId}"]`) ||
        document.getElementById(`solution-${solutionId}`) ||
        document.querySelector(`[id^="solution-"][data-id="${solutionId}"]`);

      if (el) {
        if (shouldHighlight) {
          el.classList.add('highlighted-solution');
          setHighlightedId(solutionId);
        }
        el.scrollIntoView({ behavior: cameFromSolutionView ? 'smooth' : 'auto', block: 'center' });
        if (shouldHighlight) {
          setTimeout(() => {
            el.classList.remove('highlighted-solution');
            setHighlightedId(null);
          }, 2000);
        }
        if (location.state?.fromSolutionView) {
          navigate(location.pathname + (location.search || '') + (location.hash || ''), {
            replace: true,
            state: { ...location.state, fromSolutionView: undefined }
          });
        }
        lastScrolledTargetRef.current = currentTarget;
        return;
      }

      if (attempts < maxAttempts) {
        try { requestAnimationFrame(tryHighlight); } catch { setTimeout(tryHighlight, 16); }
      } else {
        if (location.state?.fromSolutionView) {
          navigate(location.pathname + (location.search || '') + (location.hash || ''), {
            replace: true,
            state: { ...location.state, fromSolutionView: undefined }
          });
        }
        lastScrolledTargetRef.current = currentTarget;
      }
    };

    try { requestAnimationFrame(tryHighlight); } catch { setTimeout(tryHighlight, 0); }
    return () => { cancelled = true; };
  }, [location.state, location.hash, navigate, location.pathname, location.search, solutions, dispatch, postId]);

  const handleRefresh = useCallback(() => {
    if (!postId) return;
    setUserCollapsed(false);
    setVisibleCount(PAGE_SIZE);
    dispatch(fetchSolutionsFirstPage({ postId, pageSize: PAGE_SIZE }));
  }, [dispatch, postId]);

  const handleLoadMore = useCallback(() => {
    if (!postId) return;
    setUserCollapsed(false);
    const nextCount = visibleCount + PAGE_SIZE;
    if (visibleCount < solutions.length) {
      pendingScrollIndexRef.current = visibleCount;
      setVisibleCount(Math.min(nextCount, solutions.length));
      return;
    }
    if (hasMore && !loadingMore) {
      pendingScrollIndexRef.current = solutions.length;
      setVisibleCount(nextCount);
      dispatch(fetchMoreSolutionsByPostId({ postId }));
      return;
    }
    if (!hasMore && (totalCount || 0) > solutions.length && !loadingMore) {
      pendingScrollIndexRef.current = visibleCount;
      setVisibleCount(Math.min(nextCount, (totalCount || nextCount)));
      dispatch(fetchSolutionsFirstPage({ postId, pageSize: Math.max(PAGE_SIZE, nextCount) }));
    }
  }, [postId, visibleCount, PAGE_SIZE, solutions, hasMore, loadingMore, totalCount, dispatch]);

  const handleSolutionClick = useCallback((solutionId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    navigate(`/solutions/${solutionId}`, {
      state: { fromPost: postId, fromPath: location.pathname }
    });
  }, [navigate, postId, location.pathname]);

  const handleProfileClick = useCallback((userId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!userId) return;
    if (currentUser?.uid && currentUser.uid === userId) {
      navigate('/profile');
    } else {
      navigate(`/profile/${userId}`);
    }
  }, [navigate, currentUser?.uid]);

  const handleLike = useCallback(async (e, solutionId) => {
    e.stopPropagation();
    if (!currentUser?.uid) {
      toast.info('Please log in to like solutions.');
      return;
    }

    const targetSol = solutions.find(s => s.id === solutionId);
    if (!targetSol) return;
    const alreadyLiked = Array.isArray(targetSol.likers) && targetSol.likers.includes(currentUser.uid);

    try {
      setLiking(prev => ({ ...prev, [solutionId]: true }));
      await dispatch(toggleLikeSolution({ solutionId, userId: currentUser.uid, like: !alreadyLiked }));

      if (!alreadyLiked && targetSol.userId && targetSol.userId !== currentUser.uid) {
        try {
          await triggerSolutionLikeNotification(targetSol, targetSol.userId);
        } catch (_) {}
      }
    } catch (error) {
      console.error('Error liking solution:', error);
      toast.error('Failed to like solution. Please try again.');
    } finally {
      setLiking(prev => ({ ...prev, [solutionId]: false }));
    }
  }, [dispatch, currentUser, solutions, triggerSolutionLikeNotification]);

  const handleSave = useCallback(async (e, solutionId) => {
    e.stopPropagation();
    if (!currentUser?.uid) {
      toast.info('Please log in to save solutions.');
      return;
    }

    const targetSol = solutions.find(s => s.id === solutionId);
    if (!targetSol) return;
    const alreadySaved = Array.isArray(targetSol.savers) && targetSol.savers.includes(currentUser.uid);

    try {
      setSaving(prev => ({ ...prev, [solutionId]: true }));
      await dispatch(toggleSaveSolution({ solutionId, userId: currentUser.uid, save: !alreadySaved }));
    } catch (error) {
      console.error('Error saving solution:', error);
      toast.error('Failed to save solution. Please try again.');
    } finally {
      setSaving(prev => ({ ...prev, [solutionId]: false }));
    }
  }, [dispatch, currentUser, solutions]);

  const handleShare = useCallback((e, solutionId) => {
    e.stopPropagation();
    if (!currentUser?.uid) {
      toast.info('Please log in to share solutions.');
      return;
    }

    const targetSol = solutions.find(s => s.id === solutionId);
    if (!targetSol) return;

    dispatch(openShareModal({ item: targetSol, type: 'solution' }));
  }, [currentUser, solutions, dispatch]);

  const handleShowLess = () => {
    setVisibleCount(PAGE_SIZE);
    setUserCollapsed(true);
    requestAnimationFrame(() => {
      if (headerRef.current) {
        headerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  return {
    solutions,
    loading,
    error,
    hasMore,
    loadingMore,
    totalCount,
    visibleCount,
    expandedPhases,
    setExpandedPhases,
    highlightedId,
    liking,
    saving,
    sharing,
    headerRef,
    listRef,
    PAGE_SIZE,
    usersEntities,
    currentUser,
    handleRefresh,
    handleLoadMore,
    handleShowLess,
    handleSolutionClick,
    handleProfileClick,
    handleLike,
    handleSave,
    handleShare
  };
};
